"use client";
import {
  PlainDate,
  genPlainDateList,
  isNationalHoliday,
} from "@sa-nu/shared-utils/date";
import clsx from "clsx";
import Image from "next/image";
import { useMemo, useState } from "react";

export function RangeCalendar({
  min,
  max,
  checkIn,
  checkOut,
  onDateClick,
  today,
  initialMonth,
}: {
  min: PlainDate;
  max: PlainDate;
  checkIn: PlainDate | null;
  checkOut: PlainDate | null;
  onDateClick: (date: PlainDate) => void;
  today: PlainDate;
  initialMonth?: PlainDate;
}) {
  const [currentMonth, setCurrentMonth] = useState<PlainDate>(
    initialMonth ?? checkIn ?? today,
  );

  const canGoPrev = useMemo(() => {
    const prevMonthFirst = currentMonth
      .subtract({ months: 1 })
      .with({ day: 1 });
    return (
      PlainDate.compare(prevMonthFirst, min) >= 0 ||
      (prevMonthFirst.month === min.month && prevMonthFirst.year === min.year)
    );
  }, [currentMonth, min]);

  const canGoNext = useMemo(() => {
    const nextMonthFirst = currentMonth.add({ months: 1 }).with({ day: 1 });
    return PlainDate.compare(nextMonthFirst, max) <= 0;
  }, [currentMonth, max]);

  const dates = useMemo(() => {
    const [firstDay, lastDay] = getDateRangeToRender(currentMonth);
    return genPlainDateList(firstDay, lastDay);
  }, [currentMonth]);

  return (
    <div className="flex w-full flex-col gap-[8px]">
      <MonthNavigation
        currentMonth={currentMonth}
        canGoPrev={canGoPrev}
        canGoNext={canGoNext}
        onPrev={() => setCurrentMonth(currentMonth.subtract({ months: 1 }))}
        onNext={() => setCurrentMonth(currentMonth.add({ months: 1 }))}
      />
      <DateGrid
        dates={dates}
        min={min}
        max={max}
        checkIn={checkIn}
        checkOut={checkOut}
        onDateClick={onDateClick}
      />
    </div>
  );
}

function MonthNavigation({
  currentMonth,
  canGoPrev,
  canGoNext,
  onPrev,
  onNext,
}: {
  currentMonth: PlainDate;
  canGoPrev: boolean;
  canGoNext: boolean;
  onPrev: () => void;
  onNext: () => void;
}) {
  return (
    <div className="flex items-center justify-between py-[4px]">
      <button
        // 汎用の日付選択部品で、選択結果は呼び出し元の確定操作で観測する
        data-analytics-ignore
        disabled={!canGoPrev}
        onClick={onPrev}
        className="flex size-[32px] items-center justify-center rounded-full border border-[#c2c2c2] disabled:opacity-30"
      >
        <Image
          src="/i/keyboard_arrow_left.svg"
          alt="前月"
          width={16}
          height={16}
        />
      </button>
      <p className="font-notojp text-skogecha text-[18px] leading-[1.6] font-bold">
        {currentMonth.year}/{currentMonth.month}
      </p>
      <button
        // 汎用の日付選択部品で、選択結果は呼び出し元の確定操作で観測する
        data-analytics-ignore
        disabled={!canGoNext}
        onClick={onNext}
        className="flex size-[32px] items-center justify-center rounded-full border border-[#c2c2c2] disabled:opacity-30"
      >
        <Image
          src="/i/keyboard_arrow_right.svg"
          alt="次月"
          width={16}
          height={16}
        />
      </button>
    </div>
  );
}

const DAY_OF_WEEK_LABELS = ["日", "月", "火", "水", "木", "金", "土"] as const;

type DateCellVariant =
  | "selected"
  | "inRange"
  | "disabled"
  | "holiday"
  | "normal"
  | "saturday"
  | "sunday";

function getDateCellVariant({
  date,
  min,
  max,
  checkIn,
  checkOut,
}: {
  date: PlainDate;
  min: PlainDate;
  max: PlainDate;
  checkIn: PlainDate | null;
  checkOut: PlainDate | null;
}): DateCellVariant {
  const isSelected =
    (checkIn !== null && date.equals(checkIn)) ||
    (checkOut !== null && date.equals(checkOut));
  if (isSelected) return "selected";

  const isInRange =
    checkIn !== null &&
    checkOut !== null &&
    PlainDate.compare(date, checkIn) > 0 &&
    PlainDate.compare(date, checkOut) < 0;
  if (isInRange) return "inRange";

  const isOutOfRange =
    PlainDate.compare(date, min) < 0 || PlainDate.compare(date, max) > 0;
  if (isOutOfRange) return "disabled";

  if (date.dayOfWeek === 6) return "saturday";
  if (date.dayOfWeek === 7 || isNationalHoliday(date)) return "holiday";

  return "normal";
}

function DateGrid({
  dates,
  min,
  max,
  checkIn,
  checkOut,
  onDateClick,
}: {
  dates: PlainDate[];
  min: PlainDate;
  max: PlainDate;
  checkIn: PlainDate | null;
  checkOut: PlainDate | null;
  onDateClick: (date: PlainDate) => void;
}) {
  return (
    <div className="grid grid-cols-7 gap-[8px]">
      {DAY_OF_WEEK_LABELS.map((dow) => (
        <div
          key={dow}
          className={clsx(
            "text-skogecha flex h-[36px] w-[36px] items-center justify-center justify-self-center text-[14px] font-bold",
            dow === "土" && "text-blue-600",
            dow === "日" && "text-red-600",
          )}
        >
          {dow}
        </div>
      ))}
      {dates.map((date) => {
        const variant = getDateCellVariant({
          date,
          min,
          max,
          checkIn,
          checkOut,
        });
        return (
          <DateCell
            key={date.toString()}
            day={date.day}
            variant={variant}
            onClick={() => onDateClick(date)}
          />
        );
      })}
    </div>
  );
}

const dateCellStyles: Record<DateCellVariant, string> = {
  selected: "rounded-full bg-sorange text-white",
  inRange: "rounded-full bg-red-200 text-skogecha",
  disabled: "cursor-not-allowed font-normal text-stone-300",
  normal: "text-skogecha",
  saturday: "text-blue-600",
  sunday: "text-red-600",
  holiday: "text-red-600",
};

function DateCell({
  day,
  variant,
  onClick,
}: {
  day: number;
  variant: DateCellVariant;
  onClick: () => void;
}) {
  return (
    <button
      // 汎用の日付選択部品で、選択結果は呼び出し元の確定操作で観測する
      data-analytics-ignore
      disabled={variant === "disabled"}
      className={clsx(
        "flex h-[36px] w-[36px] items-center justify-center justify-self-center text-[14px] font-bold",
        dateCellStyles[variant],
      )}
      onClick={variant !== "disabled" ? onClick : undefined}
    >
      {day}
    </button>
  );
}

// カレンダーで表示するのは6週分なので、その日数を計算する
function getDateRangeToRender(keyDate: PlainDate): [PlainDate, PlainDate] {
  const firstDayOfMonth = keyDate.with({ day: 1 });
  const firstDay = firstDayOfMonth.subtract({
    days: firstDayOfMonth.dayOfWeek === 7 ? 0 : firstDayOfMonth.dayOfWeek,
  });
  const lastDay = firstDay.add({ days: 7 * 6 - 1 });
  return [firstDay, lastDay];
}
